import type { ReactNode } from 'react';
import { BiSolidChevronLeft } from 'react-icons/bi';
import { useNavigate } from 'react-router';

import { useBackHandler } from '@/hooks/useBackHandler';
import { useIsMobile } from '@/hooks/useIsMobile';
import { historyIdx, useNavigateUp } from '@/hooks/useNavigateUp';

import { TrialBanner } from '../TrialBanner/TrialBanner';

import { DesktopHeaderUserArea } from './DesktopHeaderUserArea';
import s from './Header.module.scss';

export interface HeaderProps {
  title: ReactNode;
  secondRow?: ReactNode;
  actionsWidth?: number;
  backButton?: boolean;
  backButtonNavigateTo?: string;
  backButtonNavigateState?: Record<string, unknown>;
  menuButton?: boolean;
  showActions?: boolean;
}

export const Header = ({
  title,
  secondRow,
  backButton,
  backButtonNavigateTo,
  backButtonNavigateState,
  showActions = true,
}: HeaderProps) => {
  const navigate = useNavigate();
  const navigateUp = useNavigateUp();
  const isMobile = useIsMobile();
  const goBack = () =>
    backButtonNavigateTo
      ? navigateUp(backButtonNavigateTo, { state: backButtonNavigateState })
      : void navigate(-1);
  // With nothing behind and no parent named, there is nowhere to go back to.
  const canGoBack = backButton && (Boolean(backButtonNavigateTo) || historyIdx() > 0);
  useBackHandler(canGoBack ? goBack : undefined);

  return (
    <header className={s.header}>
      <div className={s.firstRow}>
        <div className={s.titleArea}>
          {canGoBack && (
            <button className={s.iconButton} onClick={goBack} aria-label="Back">
              <BiSolidChevronLeft size={16} aria-hidden="true" />
            </button>
          )}
          <div className={s.title}>{title}</div>
        </div>
        {showActions && (
          <div className={s.actions}>
            <DesktopHeaderUserArea />
          </div>
        )}
      </div>
      {secondRow && <div className={s.secondRow}>{secondRow}</div>}
      {/* Desktop shows the trial reminder under every page's header; mobile only at the
          top of home, which places its own. */}
      {!isMobile && <TrialBanner />}
    </header>
  );
};
